import { useCallback, useState } from 'react';
import { DropTargetMonitor, XYCoord } from 'react-dnd';
import { isEqual } from 'date-fns';

import { ICalendarEventBookings, IDraggableEvent } from '~/Screens/Calendar/interfaces';
import {
  calculateFullDayActivity,
  getEventTimeInSeconds,
  IGetEventPosition,
} from '~/Screens/Calendar/CalendarView/components';
import { useSaveCalendarEvent } from './useSaveCalendarEvent';
import { useFullDayActivityBookings } from '~/Screens/Calendar/CalendarView/hooks/useFullDayActivityBookings';

interface IUseDropEvent {
  getEventPosition: IGetEventPosition;
}

export const useUpdateFullDayActivity = ({ getEventPosition }: IUseDropEvent) => {
  const [updatingFullDayActivity, setUpdatingFullDayActivity] = useState<null | IDraggableEvent>(
    null,
  );
  const [
    confirmationOptionsForFullDayActivity,
    setConfirmationOptionsForFullDayActivity,
  ] = useState<ICalendarEventBookings | void>();

  const { eventAction } = useSaveCalendarEvent();
  const { eventBookings } = useFullDayActivityBookings();

  const onCloseEditFullDayActivity = useCallback(() => {
    setConfirmationOptionsForFullDayActivity();
    setUpdatingFullDayActivity(null);
  }, [setConfirmationOptionsForFullDayActivity, setUpdatingFullDayActivity]);
  const updateFullDayActivity = useCallback(
    (item: IDraggableEvent, monitor: DropTargetMonitor) => {
      const { event, type } = item;
      const position = getEventPosition({
        item,
        differenceFromInitialOffset: monitor.getDifferenceFromInitialOffset() as XYCoord,
      });

      const newEvent = calculateFullDayActivity({
        time: getEventTimeInSeconds(item.event),
        event,
        position,
      });
      if (!isEqual(new Date(event.start), new Date(newEvent.start))) {
        setUpdatingFullDayActivity({ event: newEvent, position, type });
        eventBookings.mutate(event, {
          onSuccess: setConfirmationOptionsForFullDayActivity,
          onError: onCloseEditFullDayActivity,
        });
      }
    },
    [getEventPosition, setUpdatingFullDayActivity],
  );

  const onConfirmEditFullDayActivity = useCallback(() => {
    if (!updatingFullDayActivity) {
      return;
    }
    setConfirmationOptionsForFullDayActivity();
    eventAction.mutate(updatingFullDayActivity.event, {
      onSettled: onCloseEditFullDayActivity,
    });
  }, [onCloseEditFullDayActivity, updatingFullDayActivity, eventAction.mutate]);

  return {
    updateFullDayActivity,
    updatingFullDayActivity,
    onCloseEditFullDayActivity,
    onConfirmEditFullDayActivity,
    confirmationOptionsForFullDayActivity,
  };
};
